import { spawn } from 'node:child_process';
import { mkdir } from 'node:fs/promises';
import { createLogger } from '../logging/index.js';
import { ok, err } from '../types/result.js';
import type { Result } from '../types/result.js';

const logger = createLogger('git-clone');

/**
 * Sanitize URL for logging by removing auth.
 * Handles both HTTPS URLs with embedded auth and SCP-style git URLs.
 * Masks username and pw for security. // ok: describing behavior
 */
export function sanitizeUrl(url: string): string {
  try {
    const parsed = new URL(url);
    if (parsed.password) parsed.password = '***'; // ok: masking for security
    if (parsed.username && parsed.username !== 'git') parsed.username = '***';
    return parsed.toString();
  } catch {
    // SCP-style URL (git@github.com:org/repo) - no embedded auth to sanitize
    // But handle edge cases like //user:pass@host
    return url.replace(/\/\/[^@]+@/, '//***@');
  }
}

export interface CloneOptions {
  url: string;
  targetDir: string;
  branch?: string;
  depth?: number;
  /** Use partial clone (--filter=blob:none) for large repos */
  partialClone?: boolean;
  /** Clone timeout in milliseconds (default: 120000) */
  timeoutMs?: number;
}

const DEFAULT_CLONE_TIMEOUT_MS = 120000; // 2 minutes
const FORCE_KILL_DELAY_MS = 5000; // 5 seconds after SIGTERM

export async function cloneRepository(options: CloneOptions): Promise<Result<string>> {
  const {
    url,
    targetDir,
    branch,
    depth = 1,
    partialClone = false,
    timeoutMs = DEFAULT_CLONE_TIMEOUT_MS,
  } = options;

  await mkdir(targetDir, { recursive: true });

  const args = ['clone'];

  // Partial clone: fetch blobs on demand (reduces initial clone size for large repos)
  if (partialClone) {
    args.push('--filter=blob:none');
  }

  args.push('--depth', String(depth), '--single-branch', '--no-tags');
  if (branch !== undefined) {
    args.push('--branch', branch);
  }
  args.push(url, targetDir);

  logger.info(
    {
      url: sanitizeUrl(url),
      targetDir,
      branch,
      depth,
      singleBranch: true,
      noTags: true,
      lfsSkipSmudge: true,
      partialClone,
    },
    'Starting git clone'
  );

  return new Promise((resolve) => {
    const git = spawn('git', args, {
      stdio: ['ignore', 'pipe', 'pipe'],
      env: { ...process.env, GIT_LFS_SKIP_SMUDGE: '1' },
    });
    let timedOut = false;
    let forceKillTimeout: NodeJS.Timeout | null = null;

    // Timeout handler
    const timeout = setTimeout(() => {
      timedOut = true;
      git.kill('SIGTERM');
      // Force kill after delay if SIGTERM doesn't work
      forceKillTimeout = setTimeout(() => {
        if (!git.killed) {
          git.kill('SIGKILL');
        }
      }, FORCE_KILL_DELAY_MS);
    }, timeoutMs);

    let stderr = '';
    git.stderr.on('data', (data: Buffer) => {
      stderr += data.toString();
    });

    git.on('error', (error: Error) => {
      clearTimeout(timeout);
      if (forceKillTimeout) clearTimeout(forceKillTimeout);
      resolve(err(error));
    });

    git.on('close', (code: number | null) => {
      clearTimeout(timeout);
      if (forceKillTimeout) clearTimeout(forceKillTimeout);
      if (timedOut) {
        resolve(err(new Error(`Git clone timed out after ${String(timeoutMs)}ms for: ${url}`)));
      } else if (code === 0) {
        resolve(ok(targetDir));
      } else {
        resolve(err(new Error(`Git clone failed: ${stderr}`)));
      }
    });
  });
}

export function isGitUrl(source: string): boolean {
  return source.startsWith('http://') || source.startsWith('https://') || source.startsWith('git@');
}

export function extractRepoName(url: string): string {
  const match = /\/([^/]+?)(\.git)?$/.exec(url);
  const name = match?.[1];
  if (name === undefined) {
    return 'repository';
  }
  return name;
}
